import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Avatar,
  Popover,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import { FileDownload, FileUpload, AccessTime, Add, Close, Email, WhatsApp, Phone, LocationOn, Star, OpenInNew, Delete } from '@mui/icons-material';
import { customerAPI, activityAPI, userAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { isDummyEmail } from '../../utils/helpers';
import { usePersistedState } from '../../utils/useDebounce';
import {
  PageHeader,
  ConfirmDialog,
  BulkActionBar,
  tableHeadRowStyles,
  tableBodyRowStyles,
  tablePaginationStyles,
  tableAvatarStyles,
  primaryLinkStyles,
  standardTextStyles,
  emptyStateTextStyles,
  emptyStateLinkStyles,
} from '../../components/common';

const CustomerList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = usePersistedState('customer_list_page', 0);
  const [rowsPerPage, setRowsPerPage] = usePersistedState('customer_list_rpp', 10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = usePersistedState('customer_list_search', '');
  const [debouncedSearch, setDebouncedSearch] = usePersistedState('customer_list_dsearch', '');
  const [viewMode, setViewMode] = usePersistedState('customer_list_view', 'list');
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [activityAnchor, setActivityAnchor] = useState(null);
  const [activityCustomerId, setActivityCustomerId] = useState(null);
  
  // Schedule Activity Dialog State
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState('email');
  const [activitySummary, setActivitySummary] = useState('');
  const [activityDueDate, setActivityDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [activityAssignedTo, setActivityAssignedTo] = useState(null);
  const [activityNote, setActivityNote] = useState('');
  const [users, setUsers] = useState([]);
  const [savingActivity, setSavingActivity] = useState(false);

  // Fetch users for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await userAPI.getSalespersons();
        setUsers(response.data || []);
        // Set current user as default assignee
        if (user && response.data) {
          const currentUser = response.data.find(u => u._id === user._id || u._id === user.id);
          if (currentUser) {
            setActivityAssignedTo(currentUser);
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, [user]);

  const handleActivityClick = (event, customerId) => {
    event.stopPropagation();
    setActivityAnchor(event.currentTarget);
    setActivityCustomerId(customerId);
  };

  const handleActivityClose = () => {
    setActivityAnchor(null);
    setActivityCustomerId(null);
  };

  const handleScheduleActivity = () => {
    handleActivityClose();
    setActivityDialogOpen(true);
    // Reset form
    setActivityType('email');
    setActivitySummary('');
    setActivityDueDate(new Date().toISOString().split('T')[0]);
    setActivityNote('');
    // Keep current user as assignee
    if (user && users.length > 0) {
      const currentUser = users.find(u => u._id === user._id || u._id === user.id);
      if (currentUser) {
        setActivityAssignedTo(currentUser);
      }
    }
  };

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false);
    setActivityCustomerId(null);
  };

  const handleSaveActivity = async (markDone = false) => {
    if (!activitySummary.trim()) {
      toast.error('Please enter a summary');
      return;
    }
    if (!activityAssignedTo) {
      toast.error('Please select an assignee');
      return;
    }

    try {
      setSavingActivity(true);
      const activityData = {
        title: activitySummary,
        type: activityType,
        dueDate: activityDueDate,
        assignedTo: activityAssignedTo._id,
        description: activityNote,
        status: markDone ? 'completed' : 'pending',
        relatedTo: {
          type: 'customer',
          customer: activityCustomerId,
        },
      };

      await activityAPI.create(activityData);
      toast.success(markDone ? 'Activity marked as done' : 'Activity scheduled successfully');
      handleActivityDialogClose();
    } catch (error) {
      console.error('Error saving activity:', error);
      toast.error('Failed to save activity');
    } finally {
      setSavingActivity(false);
    }
  };

  const fetchCustomers = useCallback(async (signal) => {
    try {
      setLoading(true);
      const response = await customerAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
      });
      if (!signal?.aborted) {
        setCustomers(response.data.customers);
        setTotal(response.data.pagination.totalItems);
      }
    } catch (error) {
      if (!signal?.aborted) {
        toast.error('Failed to load customers');
        console.error('Error fetching customers:', error);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, debouncedSearch]);

  // Debounce search input: wait 400ms after the user stops typing before firing the request.
  // Skip the very first effect run so a restored `page` is not clobbered by setPage(0).
  const isFirstSearchChange = useRef(true);
  useEffect(() => {
    if (isFirstSearchChange.current) {
      isFirstSearchChange.current = false;
      return;
    }
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchCustomers(abortController.signal);
    return () => abortController.abort();
  }, [fetchCustomers]);

  // Handle row selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(customers.map(c => c._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  // Single delete
  const handleDeleteClick = (e, customer) => {
    e.stopPropagation();
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    try {
      setDeleting(true);
      await customerAPI.delete(customerToDelete._id);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete customer');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setDeleting(true);
      await customerAPI.bulkDelete(selectedRows);
      toast.success(`${selectedRows.length} customer(s) deleted successfully`);
      setSelectedRows([]);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete customers');
    } finally {
      setDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleExportAll = async () => {
    try {
      setSettingsMenuAnchor(null);
      const response = await customerAPI.export();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customers_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Customers exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export customers');
    }
  };

  const handleImportClick = () => {
    setSettingsMenuAnchor(null);
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.onchange = handleImportFile;
    fileInput.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      const text = await file.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = [];
        let current = '';
        let inQuotes = false;
        for (const char of lines[i]) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());
        
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.replace(/^"|"$/g, '') || '';
        });
        data.push(row);
      }

      const response = await customerAPI.import(data);
      toast.success(response.data.message);
      fetchCustomers(); // Refresh the list after import
      
      if (response.data.results.errors.length > 0) {
        console.warn('Import errors:', response.data.results.errors);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import customers');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Page Header */}
      <PageHeader
        title="Customers"
        newButtonText="New"
        onNew={() => navigate('/customers/new')}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search customers..."
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        viewModes={['list', 'kanban']}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onSettingsClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
      />

      {/* Content Area */}
      {viewMode === 'kanban' ? (
        /* Kanban/Card View */
        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : customers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={emptyStateTextStyles}>
                No customers found.{' '}
                <Typography
                  component="span"
                  sx={{ ...emptyStateLinkStyles, cursor: 'pointer' }}
                  onClick={() => navigate('/customers/new')}
                >
                  Add your first customer
                </Typography>
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {customers.map((customer) => {
                // Generate consistent color based on customer name
                const colors = ['#e67e22', '#27ae60', '#3498db', '#9b59b6', '#1abc9c', '#e74c3c', '#f39c12', '#2ecc71'];
                const colorIndex = customer.name ? customer.name.charCodeAt(0) % colors.length : 0;
                const avatarColor = customer.avatarColor || colors[colorIndex];
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={customer._id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid #dee2e6',
                        boxShadow: 'none',
                        bgcolor: 'white',
                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      onClick={() => navigate(`/customers/${customer._id}`)}
                    >
                      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Main Content */}
                        <Box sx={{ display: 'flex', flex: 1 }}>
                          {/* Left Avatar */}
                          <Box
                            sx={{
                              width: 80,
                              minHeight: 120,
                              bgcolor: avatarColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography sx={{ color: 'white', fontSize: '32px', fontWeight: 500 }}>
                              {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                            </Typography>
                          </Box>

                          {/* Right Content */}
                          <Box sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column' }}>
                            {/* Customer Name */}
                            <Typography sx={{ 
                              fontSize: '13px', 
                              fontWeight: 600, 
                              color: '#333',
                              mb: 0.5,
                              lineHeight: 1.3,
                            }}>
                              {customer.name}
                            </Typography>

                            {/* Email */}
                            {customer.email && !isDummyEmail(customer.email) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                <Email sx={{ fontSize: 14, color: '#666' }} />
                                <Typography sx={{ fontSize: '11px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {customer.email}
                                </Typography>
                              </Box>
                            )}

                            {/* Phone */}
                            {(customer.phone || customer.mobile) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                <Phone sx={{ fontSize: 14, color: '#666' }} />
                                <Typography sx={{ fontSize: '11px', color: '#666' }}>
                                  {customer.phone || customer.mobile}
                                </Typography>
                              </Box>
                            )}

                            {/* Location */}
                            {(customer.address?.city || customer.address?.country) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <LocationOn sx={{ fontSize: 14, color: '#e74c3c' }} />
                                <Typography sx={{ fontSize: '11px', color: '#666' }}>
                                  {[customer.address?.city, customer.address?.country].filter(Boolean).join(', ')}
                                </Typography>
                              </Box>
                            )}

                            {/* Tag/Industry */}
                            {customer.industry && (
                              <Chip
                                label={customer.industry}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '10px',
                                  bgcolor: '#f39c12',
                                  color: 'white',
                                  fontWeight: 500,
                                  borderRadius: '4px',
                                  mt: 0.5,
                                  alignSelf: 'flex-start',
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Bottom Row */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          borderTop: '1px solid #eee',
                          px: 1.5,
                          py: 1,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {/* Quotations indicator */}
                            {customer.quotationCount > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <OpenInNew sx={{ fontSize: 14, color: '#666' }} />
                                <Typography sx={{ fontSize: '11px', color: '#666' }}>
                                  {customer.quotationCount}
                                </Typography>
                              </Box>
                            )}
                            {/* Total Amount */}
                            <Typography sx={{ fontSize: '12px', color: '#333', fontWeight: 500 }}>
                              $ {customer.totalAmount || 0}
                            </Typography>
                            {/* Starred */}
                            {customer.starred && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <Star sx={{ fontSize: 14, color: '#f39c12' }} />
                                <Typography sx={{ fontSize: '11px', color: '#666' }}>
                                  1
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          {/* Activity Clock */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActivityClick(e, customer._id);
                            }}
                            sx={{ color: '#999', p: 0.5 }}
                          >
                            <AccessTime sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      ) : (
        /* List View - Table */
        <Box sx={{ backgroundColor: 'white', overflowX: 'auto' }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: { xs: 600, md: 'auto' } }}>
              <TableHead>
                <TableRow sx={tableHeadRowStyles}>
                  <TableCell padding="checkbox" sx={{ width: 40 }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedRows.length === customers.length && customers.length > 0}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Phone</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Salesperson</TableCell>
                  <TableCell align="center">Activities</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Country</TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography sx={emptyStateTextStyles}>
                        No customers found.{' '}
                        <Typography
                          component="span"
                          sx={{ ...emptyStateLinkStyles, cursor: 'pointer' }}
                          onClick={() => navigate('/customers/new')}
                        >
                          Add your first customer
                        </Typography>
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow
                      key={customer._id}
                      hover
                      selected={selectedRows.includes(customer._id)}
                      sx={tableBodyRowStyles}
                      onClick={() => navigate(`/customers/${customer._id}`)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(customer._id)}
                          onChange={() => handleSelectRow(customer._id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={tableAvatarStyles}>
                            {customer.name?.charAt(0) || 'C'}
                          </Avatar>
                          <Typography sx={primaryLinkStyles}>
                            {customer.name}
                          </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={standardTextStyles}>
                        {customer.email && !isDummyEmail(customer.email) ? customer.email : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography sx={standardTextStyles}>{customer.phone || customer.mobile || '-'}</Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography sx={standardTextStyles}>{customer.assignedSalesPerson?.name || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleActivityClick(e, customer._id)}
                        sx={{ color: '#666' }}
                      >
                        <AccessTime sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography sx={standardTextStyles}>{customer.address?.country || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(e, customer)}
                        sx={{ color: '#adb5bd', '&:hover': { color: '#dc3545' } }}
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={tablePaginationStyles}
        />
        </Box>
      )}

      {/* Settings Menu - Import/Export */}
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={() => setSettingsMenuAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            mt: 0.5,
          }
        }}
      >
        <MenuItem onClick={handleExportAll}>
          <FileDownload sx={{ fontSize: 18, color: '#666', mr: 1.5 }} />
          Export All (CSV)
        </MenuItem>
        <MenuItem onClick={handleImportClick} disabled={importing}>
          <FileUpload sx={{ fontSize: 18, color: '#666', mr: 1.5 }} />
          {importing ? 'Importing...' : 'Import from CSV'}
        </MenuItem>
      </Menu>

      {/* Activity Popover */}
      <Popover
        open={Boolean(activityAnchor)}
        anchorEl={activityAnchor}
        onClose={handleActivityClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 280,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }
        }}
      >
        <Typography sx={{ color: '#666', fontSize: '13px', mb: 1.5 }}>
          Schedule activities to help you get things done.
        </Typography>
        <Button
          startIcon={<Add />}
          onClick={handleScheduleActivity}
          sx={{
            color: '#714B67',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '13px',
            '&:hover': {
              backgroundColor: 'rgba(113, 75, 103, 0.08)',
            },
          }}
        >
          Schedule an activity
        </Button>
      </Popover>

      {/* Schedule Activity Dialog */}
      <Dialog
        open={activityDialogOpen}
        onClose={handleActivityDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 1.5,
          px: 2,
          borderBottom: '1px solid #e0e0e0',
        }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Schedule Activity</Typography>
          <IconButton size="small" onClick={handleActivityDialogClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {/* Activity Type Toggle */}
          <ToggleButtonGroup
            value={activityType}
            exclusive
            onChange={(e, newType) => newType && setActivityType(newType)}
            size="small"
            sx={{ mb: 2.5 }}
          >
            <ToggleButton 
              value="email" 
              sx={{ 
                textTransform: 'none', 
                px: 2,
                '&.Mui-selected': {
                  backgroundColor: '#714B67',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#5a3c53',
                  }
                }
              }}
            >
              <Email sx={{ fontSize: 16, mr: 0.5 }} /> Email
            </ToggleButton>
            <ToggleButton 
              value="whatsapp" 
              sx={{ 
                textTransform: 'none', 
                px: 2,
                '&.Mui-selected': {
                  backgroundColor: '#714B67',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#5a3c53',
                  }
                }
              }}
            >
              <WhatsApp sx={{ fontSize: 16, mr: 0.5 }} /> WhatsApp
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ width: 100, fontSize: '13px', color: '#333', fontWeight: 500 }}>Summary</Typography>
            <TextField
              value={activitySummary}
              onChange={(e) => setActivitySummary(e.target.value)}
              placeholder="e.g. Discuss Proposal"
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: '13px' }
              }}
            />
          </Box>

          {/* Due Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ width: 100, fontSize: '13px', color: '#333', fontWeight: 500 }}>Due Date</Typography>
            <TextField
              type="date"
              value={activityDueDate}
              onChange={(e) => setActivityDueDate(e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: '13px' }
              }}
            />
          </Box>

          {/* Assigned To */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ width: 100, fontSize: '13px', color: '#333', fontWeight: 500 }}>Assigned to</Typography>
            <Autocomplete
              value={activityAssignedTo}
              onChange={(e, newValue) => setActivityAssignedTo(newValue)}
              options={users}
              getOptionLabel={(option) => option.name || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '12px' }}>
                    {option.name?.charAt(0) || 'U'}
                  </Avatar>
                  <Typography sx={{ fontSize: '13px' }}>{option.name}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    startAdornment: activityAssignedTo ? (
                      <Avatar sx={{ width: 20, height: 20, fontSize: '10px', mr: 1 }}>
                        {activityAssignedTo.name?.charAt(0) || 'U'}
                      </Avatar>
                    ) : null,
                  }}
                />
              )}
              sx={{ flex: 1 }}
              size="small"
            />
          </Box>

          {/* Log a note */}
          <TextField
            value={activityNote}
            onChange={(e) => setActivityNote(e.target.value)}
            placeholder="Log a note..."
            variant="standard"
            fullWidth
            multiline
            rows={2}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '13px' }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={() => handleSaveActivity(false)}
            disabled={savingActivity}
            sx={{
              backgroundColor: '#714B67',
              textTransform: 'none',
              fontSize: '13px',
              px: 2,
              '&:hover': {
                backgroundColor: '#5a3c53',
              }
            }}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSaveActivity(true)}
            disabled={savingActivity}
            sx={{
              color: '#333',
              textTransform: 'none',
              fontSize: '13px',
            }}
          >
            Mark Done
          </Button>
          <Button
            onClick={handleActivityDialogClose}
            disabled={savingActivity}
            sx={{
              color: '#333',
              textTransform: 'none',
              fontSize: '13px',
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedCount={selectedRows.length}
        onClearSelection={() => setSelectedRows([])}
        onDelete={handleBulkDelete}
      />

      {/* Single Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setCustomerToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${customerToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        loading={deleting}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Customers"
        message={`Are you sure you want to delete ${selectedRows.length} customer(s)? This action cannot be undone.`}
        confirmText={`Delete ${selectedRows.length} Customer(s)`}
        type="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default CustomerList;
